// src/components/tutor/schedule/TutorScheduling.js
import React, { useMemo, useRef, useState, useEffect } from "react";
import "../../admin/scheduling/AdminScheduling.css";
import "./TutorScheduling.css";

import TutorWeekBar from "./TutorWeekBar";
import DaySection from "../../admin/scheduling/components/DaySection";

import { addDays, startOfWeekMonday } from "../../admin/scheduling/dateUtils";
import { DAYS_ORDER } from "../../admin/scheduling/constants"; 
import { fetchStudentsByIds, updateSessionStatus } from "../../admin/scheduling/schedulingApi"; 
import { dateKeyLocal } from "../../admin/scheduling/dayConfigUtils";
import { dayKeyFromDate, resolveScheduleForDate } from "../../admin/scheduling/scheduleSettingsUtils";
import {
  buildSessionNotificationsBySessionId,
  fetchDiagnosticNotificationsByStudentId,
  SCHEDULE_NOTIFICATION_WINDOWS,
} from "../../admin/scheduling/notifications/sessionNotifications";
import { fetchSessionLogsBySessionIds } from "../session_logs/sessionLogApi";

import { useTutorScheduleModel } from "../hooks/useTutorScheduleModel";

function startOfMonthLocal(d) {
  const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x;
}
function getDayKeyForDate(d) {
  return dayKeyFromDate(d);
}

export default function TutorScheduling({ currentUserUid }) {
  const {
    today, weekStart, setWeekStart, weekEnd, model, dayConfigs, refresh, weekDays
  } = useTutorScheduleModel(currentUserUid);

  const [viewMode, setViewMode] = useState("week");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [uiZoom, setUiZoom] = useState(() => {
    try {
       const raw = localStorage.getItem("tutorSchedulingZoom");
       return raw ? parseFloat(raw) : 1; 
    } catch { return 1; }
  });
  // eslint-disable-next-line no-unused-vars
  const [monthCursor, setMonthCursor] = useState(() => startOfMonthLocal(today)); // kept for handleToday cursor sync
  const [studentsById, setStudentsById] = useState({});
  const [diagnosticNotificationsByStudentId, setDiagnosticNotificationsByStudentId] = useState({});
  const [sessionLogBySessionId, setSessionLogBySessionId] = useState({});

  const dayRefs = useRef({});

  useEffect(() => {
     localStorage.setItem("tutorSchedulingZoom", String(uiZoom));
  }, [uiZoom]);

  const visibleSessions = useMemo(() => {
    const byId = new Map();
    const dayEntries = Object.values(model || {});
    for (let i = 0; i < dayEntries.length; i += 1) {
      const dayData = dayEntries[i];
      const tutorRows = Array.isArray(dayData?.tutorRows) ? dayData.tutorRows : [];
      for (let r = 0; r < tutorRows.length; r += 1) {
        const row = tutorRows[r];
        const blocks = row?.blocks || {};
        const blockEntries = Object.values(blocks);
        for (let b = 0; b < blockEntries.length; b += 1) {
          const lists = Array.isArray(blockEntries[b]?.sessionLists) ? blockEntries[b].sessionLists : [];
          for (let li = 0; li < lists.length; li += 1) {
            const sessions = Array.isArray(lists[li]) ? lists[li] : [];
            for (let s = 0; s < sessions.length; s += 1) {
              const session = sessions[s];
              const sessionId = session?.id;
              if (!sessionId || byId.has(sessionId)) continue;
              byId.set(sessionId, session);
            }
          }
        }
      }
    }
    return [...byId.values()];
  }, [model]);

  const visibleSessionIds = useMemo(() => {
    return visibleSessions.map((session) => session?.id).filter(Boolean);
  }, [visibleSessions]);

  const visibleStudentIds = useMemo(() => {
    const ids = new Set();
    const dayEntries = Object.values(model || {});
    for (let i = 0; i < dayEntries.length; i += 1) {
      const dayData = dayEntries[i];
      const tutorRows = Array.isArray(dayData?.tutorRows) ? dayData.tutorRows : [];
      for (let r = 0; r < tutorRows.length; r += 1) {
        const row = tutorRows[r];
        const blocks = row?.blocks || {};
        const blockEntries = Object.values(blocks);
        for (let b = 0; b < blockEntries.length; b += 1) {
          const lists = Array.isArray(blockEntries[b]?.sessionLists) ? blockEntries[b].sessionLists : [];
          for (let li = 0; li < lists.length; li += 1) {
            const sessions = Array.isArray(lists[li]) ? lists[li] : [];
            for (let s = 0; s < sessions.length; s += 1) {
              const studentId = sessions[s]?.studentId;
              if (studentId) ids.add(studentId);
            }
          }
        }
      }
    }
    return [...ids];
  }, [model]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!visibleStudentIds.length) {
        if (alive) setStudentsById({});
        return;
      }
      try {
        const students = await fetchStudentsByIds(visibleStudentIds);
        if (!alive) return;
        const map = {};
        for (let i = 0; i < students.length; i += 1) {
          const student = students[i];
          if (!student?.id) continue;
          map[student.id] = student;
        }
        setStudentsById(map);
      } catch (error) {
        console.error("Failed to load tutor schedule students", error);
        if (alive) setStudentsById({});
      }
    })();
    return () => {
      alive = false;
    };
  }, [visibleStudentIds]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const students = Object.values(studentsById || {});
      if (!students.length) {
        if (alive) setDiagnosticNotificationsByStudentId({});
        return;
      }

      try {
        const nextMap = await fetchDiagnosticNotificationsByStudentId(students, {
          activeDays: SCHEDULE_NOTIFICATION_WINDOWS.diagnosticDays,
          now: new Date(),
        });
        if (alive) setDiagnosticNotificationsByStudentId(nextMap || {});
      } catch (error) {
        console.error("Failed to load tutor schedule diagnostic notifications", error);
        if (alive) setDiagnosticNotificationsByStudentId({});
      }
    })();

    return () => {
      alive = false;
    };
  }, [studentsById]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!visibleSessionIds.length) {
        if (alive) setSessionLogBySessionId({});
        return;
      }
      try {
        const logs = await fetchSessionLogsBySessionIds(visibleSessionIds);
        if (alive) setSessionLogBySessionId(logs || {});
      } catch (error) {
        console.error("Failed to load tutor session log indicators", error);
        if (alive) setSessionLogBySessionId({});
      }
    })();
    return () => {
      alive = false;
    };
  }, [visibleSessionIds]);

  const sessionNotificationsBySessionId = useMemo(() => {
    return buildSessionNotificationsBySessionId({
      sessions: visibleSessions,
      studentsById,
      diagnosticNotificationsByStudentId,
      conferenceActiveDays: SCHEDULE_NOTIFICATION_WINDOWS.conferenceDays,
      now: new Date(),
    });
  }, [visibleSessions, studentsById, diagnosticNotificationsByStudentId]);

  const handleOpenLogForm = (sessionToLog) => {
    localStorage.setItem("tutorSession_" + sessionToLog.id, JSON.stringify(sessionToLog));
    window.open(`/tutor-session-form?id=${sessionToLog.id}`, "_blank");
  };

  const handleToday = () => {
    setMonthCursor(startOfMonthLocal(today));
    setWeekStart(startOfWeekMonday(today));
    if (viewMode === "month" || viewMode === "year" || DAYS_ORDER.includes(viewMode)) {
      setViewMode("week");
    }
  };

  const drillToDate = (date) => {
    setWeekStart(startOfWeekMonday(date));
    setMonthCursor(startOfMonthLocal(date));
    const dk = getDayKeyForDate(date);
    if (dk && DAYS_ORDER.includes(dk)) setViewMode(dk);
    else setViewMode("week");
  };

  // Filter out empty days when in "Week" mode
  const visibleDays = weekDays.filter(({ dayKey }) => {
    if (viewMode === "week") {
       const dayData = model[dayKey];
       const hasContent = dayData && dayData.tutorRows && dayData.tutorRows.length > 0;
       return hasContent;
    }
    if (DAYS_ORDER.includes(viewMode)) return dayKey === viewMode;
    return false;
  });


  const currentDayDate = DAYS_ORDER.includes(viewMode)
    ? (weekDays.find((x) => x.dayKey === viewMode)?.date || null)
    : null;

  const isDateClosed = (dateObj) => {
    const key = dateKeyLocal(dateObj);
    return !!resolveScheduleForDate(dateObj, dayConfigs?.[key]).isClosed;
  };
  return (
    <div
      className="tutorSchedWrap"
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        position: "relative",
      }}
    >
      {/* Floating drawer toggle button */}
      {!isDrawerOpen && (
        <button
          type="button"
          className="schedDrawerToggleBtn"
          onClick={() => setIsDrawerOpen(true)}
          aria-label="Open schedule sidebar"
          title="Open sidebar"
        >
          ☰
        </button>
      )}

      {/* Main Content Area — takes full width since drawer is now an overlay */}
      <div
        style={{
          flex: 1,
          minWidth: 900,
          display: "flex",
          flexDirection: "column",
          padding: "14px 0 14px 14px",
        }}
      >
        {/* CHANGED: Removed overflowY:auto, flex:1, minHeight:0. Added height: "auto" */}
        <div className="schedZoomOuter" style={{ "--sched-zoom": (uiZoom * 0.8).toFixed(3), height: "auto", paddingBottom: 60 }}>
          <div className="schedZoomInner">
            
            {/* WEEK / DAY VIEW */}
            {(viewMode === "week" || DAYS_ORDER.includes(viewMode)) &&
                visibleDays.map(({ dayKey, date }) => {
                const dayData = model[dayKey];
                if (!dayData) return null;

                return (
                    <DaySection
                        key={dayKey}
                        dayKey={dayKey}
                        date={date}
                        timeBlocks={dayData.timeBlocks}
                        timelineStartMin={dayData.timelineStartMin}
                        timelineEndMin={dayData.timelineEndMin}
                        tutorRows={dayData.tutorRows}
                        dayRef={(el) => (dayRefs.current[dayKey] = el)}
                        isClosed={dayData.isClosed}
                        readOnly={false} 
                        isTutorView={true} 
                        onChangeSessionStatus={async (sessionId, status) => {
                          await updateSessionStatus(sessionId, status);
                          refresh();
                        }}
                        onEditDay={null}
                        onAddTimeBlock={null}
                        onSessionDragStart={null} 
                        onEmptySlotDoubleClick={null}
                        onSessionClick={(session) => handleOpenLogForm(session)}
                        sessionNotificationsBySessionId={sessionNotificationsBySessionId}
                        sessionLogBySessionId={sessionLogBySessionId}
                        showSessionLogIndicator={true}
                        showMissingSessionLogIndicator={true}
                        sessionLogIndicatorInteractive={false}
                        sessionLogSubmittedTooltip="Session log submitted."
                        sessionLogMissingTooltip="Please write your session log."
                    />
                );
            })}
            
            {(viewMode === "week" || DAYS_ORDER.includes(viewMode)) && visibleDays.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px", color: "#6b7280", fontSize: "15px" }}>
                  No availability or sessions scheduled for this period.
              </div>
            )}

          </div>
        </div>
      </div>

      {/* TutorWeekBar — overlay drawer */}
      <TutorWeekBar
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}

        weekStart={weekStart}
        weekEnd={weekEnd}
        onPrevWeek={() => setWeekStart(addDays(weekStart, -7))}
        onNextWeek={() => setWeekStart(addDays(weekStart, 7))}
        onToday={handleToday}
        uiZoom={uiZoom}
        onUiZoomChange={setUiZoom}
        viewMode={viewMode}
        onSetViewMode={setViewMode}
        currentDayDate={currentDayDate}
        onPickDate={drillToDate}
        onPrevDay={() => {
          const anchor = currentDayDate || weekStart;
          for (let offset = 1; offset <= 14; offset += 1) {
            const candidate = addDays(anchor, -offset);
            const dk = getDayKeyForDate(candidate);
            if (!dk || isDateClosed(candidate)) continue;
            setWeekStart(startOfWeekMonday(candidate));
            setViewMode(dk);
            return;
          }
        }}
        onNextDay={() => {
          const anchor = currentDayDate || weekStart;
          for (let offset = 1; offset <= 14; offset += 1) {
            const candidate = addDays(anchor, offset);
            const dk = getDayKeyForDate(candidate);
            if (!dk || isDateClosed(candidate)) continue;
            setWeekStart(startOfWeekMonday(candidate));
            setViewMode(dk);
            return;
          }
        }}
        isDateClosed={isDateClosed}
      />

    </div>
  );
}
